import html from './intro.html';
import {emitter, EVENTS} from '../../../utils/emitter';
import {partial} from '../../../utils/partial';
import {TweenMax, Power2} from 'gsap';
import {contentContainer, getLanguage} from '../../../configs/global';
import {Window} from '../../../utils/controllers/window';
import Mustache from 'mustache';
import data from '../../../configs/copy';

import {Flag} from '../../components/flag/flag';
import {Header} from '../../components/header/header';
import {Footer} from '../../components/footer/footer';

import {clamp} from '../../../utils/math/clamp';
import {map} from '../../../utils/math/map';

export class Intro {
  private element: HTMLElement;

  private flag: Flag;
  private header: Header;
  private footer: Footer;

  private introCopyEl: HTMLElement;
  private introFooterEl: HTMLElement;
  private introTitleEl: HTMLElement;
  private ctaButtonEl: HTMLElement;

  private scrollProxyEl: HTMLElement;
  private scrollProxyHeight = 0;
  private scrollProxyScrollHeight = 0;

  private scrollValue = 0;
  private scrollTarget = 0;

  private frame = 0;

  private scrollResolve: any;
  private scrollPromise: any;

  constructor(flag: Flag, header: Header, footer: Footer) {
    this.element = partial(Mustache.render(html, data[getLanguage()]), '#intro');
    this.render();

    this.introCopyEl = this.element.querySelector(`.intro__copy`);
    this.introFooterEl = this.element.querySelector(`.intro__footer`);
    this.introTitleEl = this.element.querySelector(`.intro__campaign`);
    this.scrollProxyEl = this.element.querySelector(`.scrollProxy`);
    this.ctaButtonEl = this.element.querySelector(`.intro__footer .cta`);

    this.flag = flag;
    this.footer = footer;
    this.header = header;
    this.header.disableInteraction();

    this.scrollPromise = new Promise(r => (this.scrollResolve = r));
    this.presetAnimation();

    this.addHandlers();
    this.resize();
  }

  private render() {
    const parent = document.querySelector(contentContainer);
    parent.appendChild(this.element);
  }

  private addHandlers() {
    this.frame = window.requestAnimationFrame(this.update);
    this.ctaButtonEl.addEventListener('click', this.onCTAClick);
    emitter.on(EVENTS.resize, this.resize);
  }

  private onCTAClick = e => {
    e.preventDefault();
    this.playIntro();
  };

  private resize = (e?: any) => {
    this.scrollProxyHeight = this.scrollProxyEl.clientHeight;
    this.scrollProxyScrollHeight = this.scrollProxyEl.scrollHeight - this.scrollProxyHeight;
  };

  private enableScroll() {
    this.scrollProxyEl.style.overflowY = `scroll`;
    this.scrollProxyEl.addEventListener('scroll', this.onScroll, {passive: true});
  }

  private disableScroll() {
    this.scrollProxyEl.style.overflowY = `hidden`;
    this.scrollProxyEl.removeEventListener('scroll', this.onScroll);
  }

  private onScroll = (e: any) => {
    this.scrollTarget = clamp(0, 1, this.scrollProxyEl.scrollTop / this.scrollProxyScrollHeight);
  };

  private async onScrollCompleted() {
    this.flag.stop();
    window.cancelAnimationFrame(this.frame);
    this.header.enableInteraction();
    this.disableScroll();
    this.scrollProxyEl.removeEventListener('scroll', this.onScroll);
    this.scrollResolve();
  }

  private update = () => {
    this.frame = window.requestAnimationFrame(this.update);
    this.scrollValue += (this.scrollTarget - this.scrollValue) * 0.1;

    this.flag.step(this.scrollValue);
    this.updateTite();
    this.updateFooter();
    this.updateCampaignTitle();
    this.updateUI();

    if (this.scrollValue > 0.999) this.onScrollCompleted();
  };

  private updateTite() {
    if (this.scrollValue < 0.25) {
      let v = clamp(0, 1, map(this.scrollValue, 0.1, 0.15, 0, 1));
      TweenMax.set(this.introCopyEl, {opacity: v, scale: 1 + 0.35 * (1 - v)});
    } else {
      let v = clamp(0, 1, map(this.scrollValue, 0.25, 0.4, 1, 0));
      TweenMax.set(this.introCopyEl, {opacity: v, scale: 1 - 0.35 * (1 - v)});
    }
  }

  private updateFooter() {
    if (this.scrollValue > 0.1 || this.scrollValue == 0) return;
    let v = clamp(0, 1, map(this.scrollValue, 0.0, 0.05, 1, 0));
    TweenMax.set(this.introFooterEl, {opacity: v, y: 15 * (1 - v)});
  }

  private updateCampaignTitle() {
    if (this.scrollValue > 0.1 || this.scrollValue == 0) return;
    let v = clamp(0, 1, map(this.scrollValue, 0.0, 0.05, 1, 0));
    TweenMax.set(this.introTitleEl, {opacity: v, y: -15 * (1 - v)});
  }

  private updateUI() {
    if (this.scrollValue < 0.7) return;
    let v = clamp(0, 1, map(this.scrollValue, 0.8, 0.92, 0, 1));
    this.header.step(v);
    this.footer.step(v);
  }

  private presetAnimation() {
    TweenMax.set(this.introFooterEl, {opacity: 0, y: 10});
    TweenMax.set(this.introCopyEl, {opacity: 0});
    TweenMax.set(this.introTitleEl, {opacity: 0, y: -15});
  }

  private playIntro = () => {
    this.disableScroll();
    TweenMax.to(this, 5, {
      scrollTarget: 1,
      ease: Power2.easeIn
    });
  };

  public async animateIn(delay = 0) {
    this.flag.animateInVideo().then(() => {
      this.enableScroll();
    });
    TweenMax.to([this.introFooterEl, this.introTitleEl], 0.7, {opacity: 1, y: 0, delay: 5.2});
    return this.scrollPromise;
  }

  public kill() {
    this.element.remove();
    this.disableScroll();
    window.cancelAnimationFrame(this.frame);
  }
}
